/**
 * External Imports
 */
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { uniqBy } from 'lodash';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react';

/**
 * Internal Imports
 */
import DeptMultiSelectDropdown from '../../customDropdowns/deptMultiSelectDropdown';
import ReusableTabs from '../../reusableTabs';
import { Dropdown } from '../../shared/dropdowns';

import {
  subNavAdminLinkObjects,
  subNavReportLinkObjects
} from '../../../Route';

import { RootState } from '../../../redux/store';
import {
  setAdminFacility,
  setAllUnitsChecked,
  setDelegatedEmployee,
  setFavUnitsChecked,
  setSelectedDepts as setSelectedSupportDeptsInStore,
  setSelectedUnits,
  setCurrentFeedbackPage,
  setCurrentIssuePage,
  setEmpSelectedDepts as setEmpSelectedDeptsInStore
} from '../../../redux/actions';
import { useGetUnitsQuery } from '../../../redux/apis/Config.api';
import { useGetEmpDepartmentsQuery } from '../../../redux/apis/Admin.api';
import { useGetEmployeeConfigQuery } from '../../../redux/apis/Users.api';
import EmpDepSelect from '../../modals/reusableModalPieces/empDeptSelect/EmpDeptSelect';
import TabDropdown from '../../shared/dropdowns/tabDropdown/TabbedDropdown';
import { toTitleCaseSpecial } from '../../../utils/helpers';

import {
  DelegateDropdownFilters,
  logDelegateClick,
  logTableStart
} from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';

/**
 * Global Type Definition Imports
 */
import {
  EmpDepartment,
  // Employee,
  IDelegateOption,
  IFacility,
  IPrimaryUnit,
  Unit
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  SubTitle,
  Title,
  TitleContainer,
  StyledCheckbox,
  StyledCheckmark,
  StyledCheckboxLabel,
  StyledDivider,
  StyledOptionDiv,
  SubToolbar
} from './SubNav.styles';

interface IUnitOption {
  checked: boolean;
  id: string;
  value: string;
}

interface ISubNavProps {
  divisionTitle: string;
  isAllUnitsChecked: boolean;
  isFavUnitsChecked: boolean;
  facilities: IFacility[];
  facilityId: string;
  facilityTitle: string;
  favUnits: IPrimaryUnit[];
  roles: string[];
  selectedEmpDepartmentsFromStore: EmpDepartment[];
  selectedDelegatedEmployee: IDelegateOption;
  selectedSupportDeptsFromStore: string[];
  selectedUnits: { id: string; unit: string }[];
  supportDepts: string[];
  userId: string;
  userSection: string;
  visited: boolean;
}

const isFavUnit = (favs: IPrimaryUnit[], id: string) =>
  favs.some((primary: IPrimaryUnit) => primary.unitId === id);

const SubNav: FC<ISubNavProps> = ({
  divisionTitle,
  facilities,
  facilityId,
  facilityTitle,
  favUnits,
  isAllUnitsChecked,
  isFavUnitsChecked,
  roles,
  selectedDelegatedEmployee,
  selectedEmpDepartmentsFromStore,
  selectedSupportDeptsFromStore,
  selectedUnits,
  supportDepts,
  userId,
  userSection,
  visited
}) => {
  const dispatch = useDispatch();

  const [unitOptions, setUnitOptions] = useState<IUnitOption[]>([]);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [isFavsChecked, setIsFavsChecked] = useState<boolean>(false);
  const [selectedDelegate, setSelectedDelegate] = useState<IDelegateOption>(
    (Object.keys(selectedDelegatedEmployee).length > 0 &&
      selectedDelegatedEmployee) || {
      employeeName: 'Direct Reports List',
      hcaid: userId
    }
  );
  const [selectedFacility, setSelectedFacility] = useState('');
  const [delegateListOptions, setDelegateListOptions] = useState<
    IDelegateOption[]
  >([
    { employeeName: 'Facility List', hcaid: facilityId },
    { employeeName: 'Direct Reports List', hcaid: userId }
  ]);

  // Dropdown states
  const [isDelegateOpen, setIsDelegateOpen] = useState(false);
  const [isDeptOpen, setIsDeptOpen] = useState(false);
  const [isFacilityOpen, setIsFacilityOpen] = useState(false);
  const [isUnitOpen, setIsUnitOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [query, setQuery] = useState('');
  const [selectedSupportDepts, setSelectedSupportDepts] =
    useState<string[]>(supportDepts);
  const [selectedEmpDepts, setSelectedEmpDepts] = useState<EmpDepartment[]>([]);

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const page = pathname.split('/')[1];
  const pageTitle = page.toUpperCase();

  const navigate = useNavigate();
  const { '*': view } = useParams();

  const debouncedQuery = useDebounceValue(query, 250);

  const {
    data: { delegatedToMeEmployees: delegatedToMeList } = {}
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    // isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(userId ?? '', {
    refetchOnMountOrArgChange: 30
  });
  const {
    data: unitsData
    // isError: isUnitsError,
    // isFetching: isUnitsFetching,
    // isLoading: isUnitsLoading
  } = useGetUnitsQuery(
    {
      facilityId,
      isUnfiltered: false,
      active: true
    },
    { skip: !facilityId }
  );
  const {
    data: empDepartmentsData
    // isError: isEmpDepartmentsError,
    // isFetching: isEmpDepartmentsFetching,
    // isLoading: isEmpDepartmentsLoading
  } = useGetEmpDepartmentsQuery(
    { facilityId, onlyAdUserDepts: true },
    { skip: !facilityId }
  );

  const filteredFavUnits = favUnits.filter(
    unit => unit.facilityId === facilityId
  );

  const handleTypeAheadChange = useCallback(
    (e: any) => setQuery(e.target.value),
    []
  );

  const setOption = useCallback(
    (value: string) => {
      if (value === 'all') {
        setIsFavsChecked(false);
        setIsAllChecked(!isAllChecked);
        setUnitOptions(
          unitOptions.map(option => ({
            ...option,
            checked: !isAllChecked
          }))
        );
      } else if (value === 'favs') {
        setIsAllChecked(false);
        setIsFavsChecked(!isFavsChecked);
        setUnitOptions(
          unitOptions.map(option => {
            return isFavUnit(filteredFavUnits, option.id)
              ? {
                  ...option,
                  checked: !isFavsChecked
                }
              : {
                  ...option,
                  checked: false
                };
          })
        );
      } else {
        const copyUnitOptions = [...unitOptions];
        const optionToUpdate = copyUnitOptions.find(
          (unit: IUnitOption) => unit.id === value
        );
        if (optionToUpdate) optionToUpdate.checked = !optionToUpdate.checked;
        setUnitOptions(copyUnitOptions);
      }
    },
    [unitOptions]
  );

  const facilityHandler = (value: string) => {
    if (value) {
      setSelectedFacility(value);
      dispatch(setAdminFacility(value));
    }
    setActiveSearch('');
    setQuery('');
  };

  const handleClickDelegates = (employee: IDelegateOption) => {
    const newDelegate = {
      employeeName: employee.employeeName,
      hcaid: employee.hcaid
    };

    const isUser = employee.hcaid === userId;
    const isDelegate = delegatedToMeList?.some(
      (delegated: IDelegateOption) => delegated.hcaid === employee.hcaid
    );

    let analyticsFilterType = '';

    if (isUser) {
      analyticsFilterType = 'direct-report';
    } else if (isDelegate) {
      analyticsFilterType = 'delegate';
    }

    logTrackingEvent(
      logDelegateClick(analyticsFilterType as DelegateDropdownFilters)
    );

    dispatch(setDelegatedEmployee(newDelegate));
    setIsDelegateOpen(false);

    if (view?.includes('user') || view?.includes('delegate')) {
      if (isUser) {
        logTrackingEvent(logTableStart('/employees/list/user'));
        navigate('/employees/list/user');
      } else if (isDelegate) {
        logTrackingEvent(logTableStart('/employees/list/delegate'));
        navigate('/employees/list/delegate');
      }
    }
  };

  const alphabetizedDelegateListOptions = useMemo(
    () =>
      delegateListOptions.sort((a: IDelegateOption, b: IDelegateOption) => {
        return a.employeeName > b.employeeName ? 1 : -1;
      }),
    [delegateListOptions]
  );

  const placeholder = useMemo(() => {
    if (isAllChecked) return 'All Units';
    if (isFavsChecked && selectedUnits.length === 1)
      return selectedUnits[0].unit;
    if (isFavsChecked) return 'Favorite Units';
    if (!unitOptions.length) return '';
    if (selectedUnits.length === 0) return `No units selected`;
    if (selectedUnits.length === 1) return selectedUnits[0].unit;
    return `${selectedUnits.length} units selected`;
  }, [isAllChecked, isFavsChecked, selectedUnits]);

  const deptSearchResults = useMemo(() => {
    switch (activeSearch) {
      case 'dept':
        return supportDepts.filter((dept: string) =>
          dept.toLowerCase().includes(debouncedQuery.trim().toLowerCase())
        );
      case 'empDept':
        return (
          empDepartmentsData?.departments?.filter((dept: EmpDepartment) =>
            dept.departmentName
              .toLowerCase()
              .includes(debouncedQuery.trim().toLowerCase())
          ) ?? []
        );
      default:
        return undefined;
    }
  }, [activeSearch, supportDepts, empDepartmentsData, debouncedQuery]);

  const facilitySearchResults = useMemo(() => {
    switch (activeSearch) {
      case 'facility':
        return facilities.filter((facility: IFacility) =>
          facility.value
            .toLowerCase()
            .includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, facilities, debouncedQuery]);

  const isDirectReportOrFacilityListSelected = useMemo(
    () =>
      selectedDelegate.employeeName === 'Direct Reports List' ||
      selectedDelegate.employeeName === 'Facility List',
    [selectedDelegate.employeeName]
  );

  const module = useMemo(() => pathname.split('/')[2], [pathname]);

  const subPath = useMemo(() => pathname.split('/')[3], [pathname]);

  const supportType = useMemo(
    () => (module !== 'profile' ? module : subPath),
    [module, subPath]
  );

  const {
    patient: authorizedPatientReportSubTabs,
    employee: authorizedEmployeeReportSubTabs
  } = useMemo(
    () =>
      subNavReportLinkObjects.reduce(
        (acc, route) => {
          const hasRole = roles.some(role => route.allowedRoles.includes(role));
          if (hasRole) {
            if (route.type === 'patient') {
              if (!acc.patient.some(tab => tab.path === route.url)) {
                acc.patient.push({
                  label: route.navItemText,
                  path: route.url
                });
              }
            }
            if (route.type === 'employee') {
              if (!acc.employee.some(tab => tab.path === route.url)) {
                acc.employee.push({
                  label: route.navItemText,
                  path: route.url
                });
              }
            }
          }
          return acc;
        },
        { patient: [], employee: [] } as {
          patient: { label: string; path: string }[];
          employee: { label: string; path: string }[];
        }
      ),
    [roles, subNavReportLinkObjects]
  );

  const authorizedAdminSubTabs = useMemo(
    () =>
      uniqBy(
        subNavAdminLinkObjects.reduce((acc, route) => {
          const hasRole = roles.some(role => route.allowedRoles.includes(role));
          if (hasRole) {
            acc.push({
              label: route.navItemText,
              path: route.url
            });
          }
          return acc;
        }, [] as { label: string; path: string }[]),
        'label'
      ),
    [roles, subNavAdminLinkObjects]
  );

  const subNavTabItems = useMemo(() => {
    switch (pathname.split('/')[1]) {
      case 'patients':
        return {
          base: '/patients',
          links: !pathname.includes('/profile')
            ? [
                { label: 'Census', path: '/census' },
                { label: 'Issues', path: '/issues' },
                { label: 'Feedback', path: '/feedback' },
                { label: 'Stoplight', path: '/stoplight' }
              ]
            : []
        };
      case 'employees':
        return {
          base: '/employees',
          links: !pathname.includes('/profile')
            ? [
                ...(isDirectReportOrFacilityListSelected
                  ? [{ path: '/list/user', label: 'Direct Reports' }]
                  : [{ path: '/list/delegate', label: 'List' }]),
                ...(isDirectReportOrFacilityListSelected
                  ? [{ path: '/list/adhoc', label: 'Ad Hoc' }]
                  : []),
                { path: '/issues', label: 'Issues' },
                { path: '/feedback', label: 'Feedback' },
                { path: '/stoplight', label: 'Stoplight' }
              ]
            : []
        };
      case 'support':
        return {
          base: '/support',
          links:
            module !== 'profile'
              ? [
                  { label: 'List', path: `/${supportType}/list` },
                  { label: 'Issues', path: `/${supportType}/issues` },
                  { label: 'Feedback', path: `/${supportType}/feedback` }
                ]
              : []
        };
      case 'reports':
        return {
          base: view?.includes('patient')
            ? '/reports/patient'
            : '/reports/employee',
          links: view?.includes('patient')
            ? [...authorizedPatientReportSubTabs]
            : [...authorizedEmployeeReportSubTabs]
        };
      case 'admin':
        return {
          base: '/admin',
          links: authorizedAdminSubTabs
        };
      default:
        return { base: '/', links: [] };
    }
  }, [
    pathname,
    authorizedAdminSubTabs,
    authorizedPatientReportSubTabs,
    authorizedEmployeeReportSubTabs,
    isDirectReportOrFacilityListSelected,
    module,
    supportType,
    view
  ]);

  const pageHeaderName = useMemo(() => {
    switch (pageTitle) {
      case 'SUPPORT':
        return view?.includes('csc') ? 'CSC' : 'CNEd';
      case 'VALIDATION':
        if (view?.includes('aidet/list')) return 'AIDET®';
        if (view?.includes('bssr/list')) return 'BSSR';
        if (view?.includes('safety/list')) return 'SAFETY';
        return 'NLR/STAR COACHING';
      default:
        return pageTitle;
    }
  }, [pageTitle, view]);

  useLayoutEffect(() => {
    dispatch(setDelegatedEmployee(selectedDelegate));
  }, []);

  useLayoutEffect(() => {
    if (pathname.includes('issues')) {
      dispatch(setCurrentIssuePage(1));
    } else if (pathname.includes('feedback')) {
      dispatch(setCurrentFeedbackPage(1));
    }
  }, [pathname]);

  useLayoutEffect(() => {
    if (
      Object.keys(selectedDelegatedEmployee).length > 0 &&
      selectedDelegatedEmployee.hcaid !== selectedDelegate.hcaid
    ) {
      setSelectedDelegate(selectedDelegatedEmployee);
    }
  }, [selectedDelegatedEmployee, selectedDelegate.hcaid]);

  useEffect(() => {
    setDelegateListOptions(
      pathname.includes('issues') ||
        pathname.includes('feedback') ||
        pathname.includes('summary')
        ? [
            ...(delegatedToMeList || []),
            { employeeName: 'Facility List', hcaid: facilityId },
            { employeeName: 'Direct Reports List', hcaid: userId }
          ]
        : [
            ...(delegatedToMeList || []),
            { employeeName: 'Direct Reports List', hcaid: userId }
          ]
    );
  }, [delegatedToMeList, pathname]);

  useEffect(() => {
    if (pathname.includes('employees')) {
      if (
        pathname.includes('/employees/list') &&
        selectedDelegate.employeeName === 'Facility List'
      ) {
        dispatch(
          setDelegatedEmployee({
            employeeName: 'Direct Reports List',
            hcaid: userId
          })
        );
      }
    } else if (
      pathname.includes('support') ||
      pathname.includes('validation') ||
      pathname.includes('reports/employee/summary')
    ) {
      dispatch(
        setDelegatedEmployee({
          employeeName: 'Facility List',
          hcaid: facilityId
        })
      );
    }
  }, [pathname, selectedDelegate]);

  useEffect(() => {
    setSelectedFacility(facilityId);
    dispatch(setAdminFacility(facilityId));
  }, [facilityId]);

  useEffect(() => {
    if (unitOptions.length > 0) {
      const payload = unitOptions
        .filter((option: IUnitOption) => option.checked)
        .map(option => ({ id: option.id, unit: option.value }));
      dispatch(setSelectedUnits(payload));
      dispatch(setFavUnitsChecked(isFavsChecked));
      dispatch(setAllUnitsChecked(isAllChecked));
    }
  }, [unitOptions]);

  useLayoutEffect(() => {
    setSelectedSupportDepts(
      selectedSupportDeptsFromStore.length > 0
        ? supportDepts.filter(d => selectedSupportDeptsFromStore.includes(d))
        : supportDepts
    );
  }, [supportDepts]);

  useLayoutEffect(() => {
    dispatch(setSelectedSupportDeptsInStore(selectedSupportDepts));
  }, [selectedSupportDepts]);

  useEffect(() => {
    if (empDepartmentsData && empDepartmentsData.departments.length > 0) {
      setSelectedEmpDepts(
        selectedEmpDepartmentsFromStore.length > 0
          ? empDepartmentsData.departments.filter(dept =>
              selectedEmpDepartmentsFromStore.some(
                selected => selected.departmentId === dept.departmentId
              )
            )
          : empDepartmentsData.departments
      );
    }
  }, [empDepartmentsData]);

  useEffect(() => {
    dispatch(setEmpSelectedDeptsInStore(selectedEmpDepts));
  }, [selectedEmpDepts]);

  useEffect(() => {
    if (unitsData && unitsData.length > 0) {
      if (selectedUnits.length > 0) {
        if (!isAllUnitsChecked && !isFavUnitsChecked)
          return setUnitOptions(
            unitsData.map((unit: Unit) => ({
              id: unit.unitId,
              value: unit.unit,
              checked: selectedUnits.some(
                (u: { id: string; unit: string }) => u.id === unit.unitId
              )
            }))
          );
        if (isFavUnitsChecked) {
          setUnitOptions(
            unitsData.map((unit: Unit) => {
              return isFavUnit(filteredFavUnits, unit.unitId)
                ? {
                    id: unit.unitId,
                    value: unit.unit,
                    checked: true
                  }
                : {
                    id: unit.unitId,
                    value: unit.unit,
                    checked: false
                  };
            })
          );
          return setIsFavsChecked(true);
        }
        setUnitOptions(
          unitsData.map((unit: Unit) => ({
            id: unit.unitId,
            value: unit.unit,
            checked: true
          }))
        );
        return setIsAllChecked(true);
      }
      if (visited) {
        return setUnitOptions(
          unitsData.map((unit: Unit) => ({
            id: unit.unitId,
            value: unit.unit,
            checked: false
          }))
        );
      }
      if (filteredFavUnits.length) {
        setUnitOptions(
          unitsData.map((unit: Unit) => {
            return isFavUnit(filteredFavUnits, unit.unitId)
              ? {
                  id: unit.unitId,
                  value: unit.unit,
                  checked: true
                }
              : {
                  id: unit.unitId,
                  value: unit.unit,
                  checked: false
                };
          })
        );
        return setIsFavsChecked(true);
      }
      setUnitOptions(
        unitsData.map((unit: Unit) => ({
          id: unit.unitId,
          value: unit.unit,
          checked: true
        }))
      );
      return setIsAllChecked(true);
    }
    return undefined;
  }, [unitsData, favUnits]);

  return (
    <SubToolbar id="SubNav">
      <TitleContainer>
        <Title id={pageHeaderName} data-testid={pageHeaderName}>
          {pageHeaderName}
        </Title>
        {!pathname.includes('admin') && (
          <SubTitle>
            {divisionTitle} | {facilityTitle}
          </SubTitle>
        )}
      </TitleContainer>
      {((pageTitle === 'SUPPORT' &&
        (view?.includes('csc/list') || view?.includes('cned/list'))) ||
        (pageTitle === 'VALIDATION' &&
          (pathname?.includes('aidet/list') ||
            pathname?.includes('bssr/list') ||
            pathname?.includes('nlr/list')))) &&
      supportDepts &&
      supportDepts.length > 0 ? (
        <div
          className="d-flex"
          style={{ flexBasis: '25%', marginLeft: '58px' }}
        >
          {/* Support and Validation departments this is different from Employee department list */}
          <DeptMultiSelectDropdown
            id={`${view}-dept-dropdown`}
            closeOnClick={false}
            focused={activeSearch === 'dept'}
            name="dept"
            open={isDeptOpen}
            options={supportDepts}
            placeholder={
              selectedSupportDeptsFromStore.length ===
              selectedSupportDepts.length
                ? selectedSupportDeptsFromStore.length === supportDepts.length
                  ? 'All Departments'
                  : selectedSupportDeptsFromStore.length > 1
                  ? `${selectedSupportDeptsFromStore.length} Departments`
                  : selectedSupportDeptsFromStore.length === 1 &&
                    selectedSupportDeptsFromStore[0]
                  ? selectedSupportDeptsFromStore[0]
                  : 'Select a department'
                : ''
            }
            query={query}
            searchResults={deptSearchResults as string[]}
            selectedDepts={selectedSupportDeptsFromStore}
            typeAhead
            setActiveSearch={setActiveSearch}
            setOpen={setIsDeptOpen}
            setQuery={setQuery}
            setSelectedDepts={setSelectedSupportDepts}
            setTypeAhead={setActiveSearch}
            setTypeAheadChange={handleTypeAheadChange}
          />
        </div>
      ) : page === 'admin' && (view === 'areas' || view === 'discharges') ? (
        <div
          className="d-flex"
          style={{ flexBasis: '25%', marginLeft: '58px' }}
        >
          {/* TODO: Move to a custom dropdown */}
          <Dropdown
            id="Facility-Selector"
            focused={activeSearch === 'facility'}
            name="facility"
            open={isFacilityOpen}
            placeholder={
              facilities.find(
                (f: IFacility) => f.facilityId === selectedFacility
              )?.value || ''
            }
            query={query}
            typeAhead
            setOpen={setIsFacilityOpen}
            setTypeAhead={setActiveSearch}
            setTypeAheadChange={handleTypeAheadChange}
          >
            {activeSearch === 'facility' &&
            facilitySearchResults &&
            facilitySearchResults.length > 0 ? (
              facilitySearchResults.map((item: IFacility) => (
                <div
                  key={item.id}
                  id={`Facility-List-Item-${item.id}`}
                  className={`dropdown-hover-item${
                    selectedFacility === item.facilityId ? ' selected' : ''
                  }`}
                  aria-selected={selectedFacility === item.facilityId}
                  role="option"
                  tabIndex={0}
                  onClick={() => facilityHandler(item.facilityId)}
                  onKeyDown={() => facilityHandler(item.facilityId)}
                >
                  {toTitleCaseSpecial(item.value)}
                </div>
              ))
            ) : (
              <div className="dropdown-search-no-result">No results found!</div>
            )}
          </Dropdown>
        </div>
      ) : userSection === 'patient' ||
        (userSection === 'reports' && !view?.includes('employee')) ? (
        <div
          className="d-flex"
          style={{ flexBasis: '25%', marginLeft: '58px' }}
        >
          {/* TODO: Move to a custom dropdown */}
          <Dropdown
            id="unit-options-selector"
            name="unitOption"
            closeOnClick={false}
            open={isUnitOpen}
            placeholder={placeholder}
            setOpen={setIsUnitOpen}
          >
            <StyledOptionDiv key="Favs">
              <StyledCheckbox
                id="fav-unit-checkbox"
                name="option-favs"
                checked={isFavsChecked}
                type="checkbox"
                readOnly
              />
              <StyledCheckmark onClick={() => setOption('favs')} />
              <StyledCheckboxLabel htmlFor="fav-unit-checkbox">
                Select My Favorite Units
              </StyledCheckboxLabel>
            </StyledOptionDiv>
            <StyledOptionDiv key="All">
              <StyledCheckbox
                id="all-option-checkbox"
                name="option-all"
                checked={isAllChecked}
                type="checkbox"
                readOnly
              />
              <StyledCheckmark onClick={() => setOption('all')} />
              <StyledCheckboxLabel htmlFor="all-option-checkbox">
                Select All Units
              </StyledCheckboxLabel>
            </StyledOptionDiv>
            <StyledDivider />

            {[...unitOptions]
              .sort((a: IUnitOption, b: IUnitOption) =>
                a.value > b.value ? 1 : -1
              )
              .map((option: IUnitOption) => (
                <StyledOptionDiv key={option.id}>
                  <StyledCheckbox
                    id={`unit-options-${option.id}`}
                    name={option.value}
                    checked={option.checked}
                    type="checkbox"
                    disabled={isAllChecked || isFavsChecked}
                    readOnly
                  />
                  <StyledCheckmark
                    disabled={isAllChecked || isFavsChecked}
                    onClick={
                      isAllChecked || isFavsChecked
                        ? () => {}
                        : () => setOption(option.id)
                    }
                  />
                  <StyledCheckboxLabel htmlFor={`unit-options-${option.id}`}>
                    {option.value}
                  </StyledCheckboxLabel>
                </StyledOptionDiv>
              ))}
          </Dropdown>
        </div>
      ) : (
        userSection === 'employee' &&
        (view === 'list/user' || view === 'list/delegate') && (
          <div className="d-flex ms-5" style={{}}>
            {/* TODO: Move to a custom dropdown */}
            <Dropdown
              open={isDelegateOpen}
              placeholder={selectedDelegate.employeeName}
              setOpen={setIsDelegateOpen}
              styles={{
                minWidth: '22em',
                marginLeft: '25px'
              }}
            >
              {alphabetizedDelegateListOptions.map(
                (employee: IDelegateOption) => (
                  <div
                    key={employee.hcaid}
                    id={`Delegate-List-Item-${employee.hcaid}`}
                    data-testid={`select-${
                      employee.employeeName.toLowerCase() ===
                      'direct reports list'
                        ? 'direct-report'
                        : `delegate-${employee.hcaid}`
                    }`}
                    className={`dropdown-hover-item${
                      selectedDelegate.hcaid === employee.hcaid
                        ? ' selected'
                        : ''
                    }`}
                    aria-selected={selectedDelegate.hcaid === employee.hcaid}
                    role="option"
                    tabIndex={0}
                    onClick={() => handleClickDelegates(employee)}
                    onKeyDown={() => handleClickDelegates(employee)}
                  >
                    {toTitleCaseSpecial(employee.employeeName) ||
                      toTitleCaseSpecial(employee.hcaid) ||
                      'My List'}
                  </div>
                )
              )}
            </Dropdown>
          </div>
        )
      )}
      {userSection === 'reports' &&
        !view?.includes('patient') &&
        !view?.includes('employee/frequency') &&
        empDepartmentsData &&
        empDepartmentsData.departments.length > 0 && (
          <div
            className="d-flex"
            style={{
              flexBasis: '25%',
              marginLeft: '58px'
            }}
          >
            {/* Employee department list for Employee reporting */}
            <EmpDepSelect
              id={`${view}-dept-dropdown`}
              closeOnClick={false}
              focused={activeSearch === 'empDept'}
              name="empDept"
              open={isDeptOpen}
              options={empDepartmentsData.departments}
              placeholder={
                selectedEmpDepartmentsFromStore.length ===
                selectedEmpDepts.length
                  ? selectedEmpDepartmentsFromStore.length ===
                    empDepartmentsData.departments.length
                    ? 'All Departments'
                    : selectedEmpDepartmentsFromStore.length > 1
                    ? `${selectedEmpDepartmentsFromStore.length} Departments`
                    : selectedEmpDepartmentsFromStore.length === 1 &&
                      selectedEmpDepartmentsFromStore[0].departmentName
                    ? selectedEmpDepartmentsFromStore[0].departmentName
                    : 'Select a department'
                  : ''
              }
              query={query}
              searchResults={deptSearchResults as EmpDepartment[]}
              selectedDepts={selectedEmpDepts}
              typeAhead
              setActiveSearch={setActiveSearch}
              setOpen={setIsDeptOpen}
              setQuery={setQuery}
              setSelectedDepts={setSelectedEmpDepts}
              setTypeAhead={setActiveSearch}
              setTypeAheadChange={handleTypeAheadChange}
            />
          </div>
        )}
      {userSection === 'employee' &&
        (view === 'issues' || view === 'feedback') && (
          <div
            className="d-flex"
            style={{
              flexBasis: '25%',
              marginLeft: '58px'
            }}
          >
            <TabDropdown
              handleClickDelegate={handleClickDelegates}
              selectedDepts={selectedEmpDepts}
              setSelectedDepts={setSelectedEmpDepts}
            />
          </div>
        )}
      <ReusableTabs tabItems={subNavTabItems} justify="end" />
    </SubToolbar>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  roles: state.AuthorizedUser.authorizedUser.roles,
  facilities: state.ConfigReducer.userAccessibleFacilities,
  facilityId: state.AuthorizedUser.authorizedUser.facilityId,
  favUnits: state.AuthorizedUser.authorizedUser.primaryUnit,
  isAllUnitsChecked: state.ConfigReducer.isAllUnitsChecked,
  isFavUnitsChecked: state.ConfigReducer.isFavUnitsChecked,
  selectedDelegatedEmployee: state.EmployeeReducer.selectedDelegatedEmployee,
  selectedEmpDepartmentsFromStore: state.ConfigReducer.selectedEmpDepartments,
  selectedSupportDeptsFromStore: state.EmployeeReducer.selectedDepts,
  selectedUnits: state.ConfigReducer.selectedUnits,
  supportDepts: state.EmployeeReducer.depts,
  userId: state.AuthorizedUser.authorizedUser.hcaid,
  userSection: state.UserReducer.userSection,
  visited: state.ConfigReducer.unitsSelected
});

export default connect(mapReduxStateToProps)(SubNav);
